/**
 * The patient's source list + upload (Sources subtab).
 *
 * Lists `type='source'` documents and provides an upload box that accepts a
 * pasted transcript or a text file (read in-browser) → uploaded as a new source.
 * Selecting a source opens it in the {@link SourceViewer}. Source content is
 * PHI; only non-PHI titles/dates are shown here, and nothing is logged.
 *
 * **Folders grouped by absorption source kind (ADR-049/050).** Sources are
 * first grouped by title ({@link groupSourcesByTitle}), then those title-groups
 * are grouped into folders by the persisted `source_kind`
 * ({@link groupTitleGroupsByDimension}), ordered most-recent-activity-first,
 * with all clinical assessment instruments grouped under a single "Assessments"
 * parent folder. A kind with only one title-group renders as a plain row, not a
 * folder — except the "Assessments" folder which always renders as a folder even
 * for a single assessment. Each folder's most-recent title-group is directly
 * clickable while the folder stays collapsed, with a separate chevron (never
 * nested in the row button) to reveal the rest; selecting an assessment auto-
 * expands the Assessments folder.
 *
 * **Display-layer polish (ADR-056/059).** The row label is DERIVED from
 * `source_kind` (the folder header, the type) + the encounter date parsed from
 * the stored title — the stored title is never renamed (it is the natural key,
 * the idempotent-skip key, and the legacy-backfill parse input). So the type is
 * shown once in single-kind folder headers (the row is the date), child rows
 * inside the mixed Assessments folder retain their instrument label + date, and
 * the ingestion status is stated once rather than repeated per row:
 *
 * - **D1 — date from the title, fail-visible.** A single-kind folder-child row
 *   shows only the encounter date (parsed from the title's ` — {date}` suffix,
 *   NOT `created_at`, the absorption timestamp), while child rows inside the
 *   mixed Assessments folder retain their instrument label + date. A kinded title
 *   that does not parse falls back to the verbatim title + an explicit "Date
 *   unavailable" — never a blank, a wrong date, or a silent `created_at`.
 *   Unknown/uploaded sources (no deterministic title) keep their verbatim title
 *   + an "Added {date}" note.
 * - **D2 — same-day, same-type collisions render as sequenced sibling rows** (a
 *   correctness fix). Two distinct encounters of one type on one day produce
 *   identical titles; the old behaviour collapsed them into one row + a "Show
 *   earlier version" disclosure, which told a clinician something FALSE (a real
 *   second encounter mislabelled as a version). They now render as sequenced
 *   siblings, both visible and independently selectable. **BLA-333:** the
 *   "Encounter N of M" ordinal that used to be visible inline chrome (eating
 *   width the title needed) is now a hover **tooltip** on the row instead — the
 *   rows stay distinguishable on hover without competing for horizontal space.
 *   Genuine version history (multiple versions of ONE document handle) lives in
 *   the SourceViewer's version list, a separate surface untouched here; a
 *   corrected re-absorb currently lands as a second document handle and so also
 *   shows as a sibling until `pcp-source-reabsorb-versioning` collapses it into
 *   a version.
 * - **D3 — status by exception.** When every source shares one ingestion state
 *   (the materialise-only pilot: all `not_ingested`), it is stated once in a
 *   panel-level summary and NO per-row badge repeats. When the states are mixed,
 *   only the deviating (`not_ingested`) rows carry a badge.
 * - **D4 — encounter-date ordering + year sub-grouping.** Folders, the rows within
 *   them, and the year buckets are all ordered/keyed by the parsed encounter date
 *   (newest-first, undated last) — NOT `created_at`, the absorption timestamp,
 *   which is ~identical across a bulk pull. An expanded folder with more than
 *   eight documents spanning at least two encounter years sub-groups by year
 *   ("2026 (9) / …"). See `sourceGrouping.compareByEncounterDateDesc`.
 * - **D5 — recency language.** The most-recent row of each folder/plain-group
 *   shows a relative pill ("Today" / "N days ago" / "Latest"); other rows show
 *   their absolute date.
 * - **D6 — one folder affordance.** The folder header carries the count badge;
 *   an amber exception marker appears only when the folder deviates from the
 *   shared ingestion state (the always-on rollup dot is retired).
 * - **D7 — quiet panel summary & hover delete.** The panel-level status summary
 *   is shrunk to a single quiet line. The per-row Delete button is visually quiet
 *   (`opacity-0`) by default and revealed on hover/focus-within, but stays in the
 *   tab order so keyboard reachability survives.
 *
 * **Two-line row redesign (ADR-066).** The 288px rail
 * packed the recency pill, sibling ordinal, kind label, "not ingested" badge,
 * and the date onto ONE flex line where only the label truncated — with two or
 * more badges present, the label and the trailing date both clipped. Fixed by
 * splitting each row into a **primary line** (the row's flexible identifying
 * text — the kind label or, inside a folder, the date itself — plus compact
 * fixed-width decorations: the recency pill, a companion date when a label is
 * also present, and the ingestion-state icon) and a **secondary muted line**
 * (the sibling ordinal and/or the D1 fail-visible note), which never compete
 * with the primary text for width. The "not ingested" exception is now an
 * **icon + `aria-label` + tooltip** rather than a full badge — the glyph
 * itself carries the fact (never tooltip-only, since a tooltip is unreachable
 * on touch), the tooltip only elaborates. The rail also bumps `w-72`→`w-80`
 * (a small width concession).
 */
import React, { useEffect, useMemo, useState } from "react";
import apiUtils, { isApiError } from "../../apiUtils";
import { SHOW_BLUEPRINT_EXPLORE_TABS } from "../../featureFlags";
import type { BlueprintDocument } from "../../types";

export interface ContentSearchResult {
  document_id: string;
  title: string;
  encounter_date: string | null;
  snippet: string;
  source_kind?: string | null;
}

import { humanizeStatus } from "../../utils/statusLabels";
import { recordDuration } from "../../utils/perfMetrics";
import {
  distinctEncounterYears,
  groupSourcesByTitle,
  groupTitleGroupsByDimension,
  kindDimension,
  subgroupTitleGroupsByYear,
  type SourceFolder,
  type SourceTitleGroup,
} from "./sourceGrouping";
import {
  formatDate,
  parseEncounterDateFromTitle,
  sourceRowContent,
} from "./sourceDisplay";
import {
  ASSESSMENTS_FOLDER_KEY,
  isAssessmentKind,
  sourceKindIcon,
} from "../../utils/sourceKindLabels";

/** A folder's document count above which it sub-groups by year when expanded (D4). */
const YEAR_SUBGROUP_THRESHOLD = 8;

/** Props for {@link SourceList}. */
interface SourceListProps {
  /** All of the patient's documents (filtered to sources here). */
  documents: BlueprintDocument[];
  /** The currently selected source id, if any. */
  selectedId: string | null;
  /** Select a source to view, optionally targeting a search snippet to reveal and highlight. */
  onSelect: (documentId: string, snippet?: string) => void;
  /** Optional real-time search query to filter sources. */
  searchQuery?: string;
  /** Optional patient id for server-side content search. */
  patientId?: string;
  /** Sort field: "date" (default) or "alphabetical". */
  sortField?: "date" | "alphabetical";
  /** Sort direction: "desc" (default) or "asc". */
  sortDirection?: "asc" | "desc";
}

/**
 * Shared per-render context threaded to every row so the leaf components stay
 * small (avoids prop-drilling the same handlers/state through each level).
 */
interface RowContext {
  /** The currently selected source id. */
  selectedId: string | null;
  /** Select a source to view. */
  onSelect: (documentId: string) => void;
  /**
   * True when the patient's sources are in MIXED ingestion states, so a
   * per-row not-ingested badge carries information (D3). False when every source
   * shares one state (stated once at panel level) — then no per-row badge shows.
   */
  mixed: boolean;
}

// BLA-315 / ADR-069: the per-source `DeleteControl` inline-confirm affordance
// (opacity-reveal Delete → Confirm/Cancel) is removed for this first rollout
// — sources are read-only, and the server refuses the delete regardless
// (`_guard_undeletable_document` in `backend/app/routers/documents.py`). See
// ADR-069 for exactly how to restore it (git-revert this file's history for
// the removed component + its call site in `SourceRow`, and drop the
// matching backend guard branch) once the platform is ready to allow it.

/** A single source row: date-first select button (read-only, BLA-315/ADR-069). */
function SourceRow({
  doc,
  ctx,
  inFolder,
  ordinal,
}: {
  doc: BlueprintDocument;
  ctx: RowContext;
  /** Whether this row sits inside a folder (folder header carries the type). */
  inFolder: boolean;
  /** Sequence position when this row is one of same-titled sibling encounters (D2). */
  ordinal?: { index: number; total: number };
}): React.ReactElement {
  // Memoized per-document (not per-render): `sourceRowContent` parses the
  // title's encounter date internally, and this row re-renders on every
  // unrelated SourceList state change (selection, hover, folder toggle) over
  // a document set that can run into the hundreds.
  const content = useMemo(
    () => sourceRowContent(doc, { inFolder }),
    [doc, inFolder],
  );
  const selected = doc.id === ctx.selectedId;
  const showBadge = ctx.mixed && doc.ingestion_status === "not_ingested";

  // Primary line owns a SINGLE flexible truncating slot: the kind label when
  // present (a plain, ungrouped row), or — inside a folder, whose header
  // already carries the type — the date itself. A companion compact date
  // renders alongside a present label so the encounter date is never lost.
  const showCompanionDate = content.label !== "" && content.date !== "";
  const hasSecondaryLine = content.note !== "";
  // BLA-333: the sibling ordinal is no longer inline chrome (it ate width the
  // title needed); it survives ONLY as a hover tooltip on the row, so two
  // same-date same-type encounters stay distinguishable without competing for
  // horizontal space.
  const ordinalTooltip = ordinal
    ? `Encounter ${ordinal.index} of ${ordinal.total} — two distinct encounters share this date and type`
    : undefined;

  return (
    <li
      className={`flex flex-col gap-0.5 ${
        inFolder ? "pl-2 border-l-2 border-slate-200 ml-1.5" : ""
      } ${ordinal ? "ml-2" : ""}`}
    >
      <div className="group flex items-center gap-1">
        <button
          type="button"
          onClick={() => ctx.onSelect(doc.id)}
          aria-pressed={selected}
          title={ordinalTooltip}
          data-testid={ordinal ? "source-sibling-row" : undefined}
          className={`flex-1 min-w-0 text-left rounded-lg px-2.5 py-1.5 text-sm transition-colors ${
            selected
              ? "bg-blue-50 border border-blue-300 font-medium"
              : "hover:bg-slate-100 border border-transparent"
          }`}
        >
          {/* Line 1 — primary: the identifying text, never clipped by badges. */}
          <span className="flex items-center gap-1.5 min-w-0">
            {inFolder && (
              <span
                className="text-xs text-slate-400 select-none shrink-0"
                aria-hidden="true"
                data-testid="source-doc-icon"
              >
                📄
              </span>
            )}
            <span className="flex-1 min-w-0 truncate text-slate-800">
              {content.label ? (
                content.label
              ) : (
                <span data-testid="source-row-date">{content.date}</span>
              )}
            </span>
            {showBadge ? (
              <span
                data-testid="source-not-ingested-badge"
                title="Not yet reviewed for the clinical profile — nothing from it has been added yet."
                aria-label="Not in profile"
                role="img"
                className="shrink-0 text-amber-500 leading-none"
              >
                ◔
              </span>
            ) : null}
            {showCompanionDate ? (
              <span
                data-testid="source-row-date"
                className="shrink-0 text-[11px] text-slate-500 tabular-nums"
              >
                {content.date}
              </span>
            ) : null}
          </span>
          {/* Line 2 — secondary: muted, low-frequency signals only. */}
          {hasSecondaryLine ? (
            <span className="flex items-center gap-1 min-w-0 mt-0.5 text-[11px]">
              {content.note ? (
                <span
                  data-testid="source-row-note"
                  className={`truncate ${
                    content.unparsed ? "text-amber-700" : "text-slate-400"
                  }`}
                >
                  {content.note}
                </span>
              ) : null}
            </span>
          ) : null}
        </button>
      </div>
    </li>
  );
}

/**
 * Render one title-group's document(s).
 *
 * A single document renders as one row. Same-titled documents (a same-day
 * same-type collision, or an as-yet-unversioned corrected re-absorb) render as
 * sequenced sibling rows, oldest-first for a natural reading, all visible and
 * independently selectable (D2) — never hidden behind a "version" disclosure
 * that would mislabel a distinct encounter. The distinguishing "Encounter N of
 * M" marker is a hover tooltip on each sibling row (BLA-333), not inline text.
 */
function TitleGroupRows({
  group,
  ctx,
  inFolder,
}: {
  group: SourceTitleGroup;
  ctx: RowContext;
  inFolder: boolean;
}): React.ReactElement {
  const docs = [group.latest, ...group.earlier];
  if (docs.length === 1) {
    return <SourceRow doc={group.latest} ctx={ctx} inFolder={inFolder} />;
  }
  const oldestFirst = [...docs].reverse();
  return (
    <>
      {oldestFirst.map((doc, index) => (
        <SourceRow
          key={doc.id}
          doc={doc}
          ctx={ctx}
          inFolder={inFolder}
          ordinal={{ index: index + 1, total: docs.length }}
        />
      ))}
    </>
  );
}

/** A folder header: icon, label, exception marker (deviation only), count, chevron. */
function FolderHeader({
  folder,
  expanded,
  onToggle,
  onSelect,
  showException,
}: {
  folder: SourceFolder;
  expanded: boolean;
  onToggle: () => void;
  onSelect?: (documentId: string) => void;
  showException: boolean;
}): React.ReactElement {
  const latestId = folder.groups[0]?.latest.id;
  const handleHeaderClick = (): void => {
    onToggle();
    if (latestId && onSelect) {
      onSelect(latestId);
    }
  };

  return (
    <div
      onClick={handleHeaderClick}
      data-testid="source-folder-header"
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-t-lg border-b cursor-pointer select-none transition-colors ${
        expanded
          ? "bg-blue-50 border-blue-100 hover:bg-blue-100/70"
          : "bg-slate-50 border-slate-100 hover:bg-slate-100"
      }`}
    >
      <span className="text-xs" aria-hidden="true">
        {sourceKindIcon(folder.key)}
      </span>
      <span className="flex-1 text-xs font-semibold text-slate-700 truncate">
        {folder.label}
      </span>
      {showException ? (
        <span
          data-testid="source-folder-exception"
          title="Some sources in this folder have not been reviewed for the clinical profile."
          className="shrink-0 rounded-full bg-amber-100 border border-amber-300 px-1.5 text-[10px] font-semibold text-amber-800"
        >
          {humanizeStatus("not_ingested")}
        </span>
      ) : null}
      <span
        data-testid="source-folder-count"
        className="text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-1.5"
      >
        {folder.documentCount}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label={`${expanded ? "Collapse" : "Expand"} ${folder.label}`}
        data-testid="source-folder-toggle"
        className={`chevron text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded w-5 h-5 grid place-items-center transition-transform ${
          expanded ? "rotate-90" : ""
        }`}
      >
        ›
      </button>
    </div>
  );
}

/** Render the folder's title-groups grouped into year sub-sections (D4). */
function YearGroupedBody({
  folder,
  ctx,
}: {
  folder: SourceFolder;
  ctx: RowContext;
}): React.ReactElement {
  // Hoisted + memoized: previously computed inline in the JSX below, so it
  // re-ran on every render of every expanded, over-threshold folder.
  const yearGroups = useMemo(
    () => subgroupTitleGroupsByYear(folder.groups),
    [folder.groups],
  );
  return (
    <div className="space-y-1">
      {yearGroups.map((year) => (
        <div key={year.label} data-testid="source-folder-year-group">
          <div className="flex items-center justify-between px-2 pt-1 pb-0.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
              {year.label}
            </span>
            <span className="text-[10px] text-slate-400">
              {year.documentCount}
            </span>
          </div>
          <ul className="space-y-1">
            {year.groups.map((group) => (
              <TitleGroupRows
                key={group.latest.id}
                group={group}
                ctx={ctx}
                inFolder
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** One instrument sub-folder item inside the parent Assessments folder. */
function AssessmentSubfolderItem({
  subfolder,
  expanded,
  onToggle,
  ctx,
}: {
  subfolder: SourceFolder;
  expanded: boolean;
  onToggle: () => void;
  ctx: RowContext;
}): React.ReactElement {
  const showException = ctx.mixed && subfolder.hasPending;
  const latestDoc = subfolder.groups[0]?.latest;
  const latestId = latestDoc?.id;
  const latestDateObj = latestDoc
    ? parseEncounterDateFromTitle(latestDoc.title)
    : null;
  const latestDate = latestDateObj ? formatDate(latestDateObj) : "";

  const isSelected = subfolder.groups.some(
    (g) =>
      g.latest.id === ctx.selectedId ||
      g.earlier.some((d) => d.id === ctx.selectedId),
  );

  return (
    <li
      data-testid="source-subfolder"
      className="rounded-lg border border-slate-200 overflow-hidden"
    >
      <div
        data-testid="source-subfolder-header"
        onClick={() => {
          onToggle();
          if (latestId) {
            ctx.onSelect(latestId);
          }
        }}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 cursor-pointer select-none transition-colors text-sm ${
          expanded
            ? "bg-slate-100/80 border-b border-slate-200"
            : isSelected
              ? "bg-blue-50 border-transparent font-medium"
              : "hover:bg-slate-50"
        }`}
      >
        <span className="flex-1 min-w-0 text-xs font-semibold text-slate-700 truncate">
          {subfolder.label}
        </span>
        {showException ? (
          <span
            data-testid="source-folder-exception"
            title="Some sources in this folder have not been reviewed for the clinical profile."
            className="shrink-0 rounded-full bg-amber-100 border border-amber-300 px-1.5 text-[10px] font-semibold text-amber-800"
          >
            {humanizeStatus("not_ingested")}
          </span>
        ) : null}
        {latestDate ? (
          <span
            data-testid="source-row-date"
            className="shrink-0 text-[11px] text-slate-500 tabular-nums"
          >
            {latestDate}
          </span>
        ) : null}
        <span
          data-testid="source-subfolder-count"
          className="text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-1.5"
        >
          {subfolder.documentCount}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-label={`${expanded ? "Collapse" : "Expand"} ${subfolder.label}`}
          data-testid="source-subfolder-toggle"
          className={`chevron text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded w-5 h-5 grid place-items-center transition-transform ${
            expanded ? "rotate-90" : ""
          }`}
        >
          ›
        </button>
      </div>

      {expanded && (
        <div className="px-1.5 pb-1.5 pt-1 bg-white">
          <ul className="space-y-1">
            {subfolder.groups.map((group) => (
              <TitleGroupRows
                key={group.latest.id}
                group={group}
                ctx={ctx}
                inFolder
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

/** The expanded folder body: flat, year-grouped, or nested instrument subfolders for Assessments. */
function FolderBody({
  folder,
  expandedSubfolders,
  onToggleSubfolder,
  ctx,
}: {
  folder: SourceFolder;
  expandedSubfolders?: Set<string>;
  onToggleSubfolder?: (key: string) => void;
  ctx: RowContext;
}): React.ReactElement {
  if (
    folder.key === ASSESSMENTS_FOLDER_KEY &&
    folder.subfolders &&
    folder.subfolders.length > 0
  ) {
    return (
      <div className="px-2 pb-2 pt-1.5">
        <ul className="space-y-1.5" data-testid="source-assessments-subfolders">
          {folder.subfolders.map((subfolder) => (
            <AssessmentSubfolderItem
              key={subfolder.key}
              subfolder={subfolder}
              expanded={expandedSubfolders?.has(subfolder.key) ?? false}
              onToggle={() => onToggleSubfolder?.(subfolder.key)}
              ctx={ctx}
            />
          ))}
        </ul>
      </div>
    );
  }

  const yearGrouped =
    folder.documentCount > YEAR_SUBGROUP_THRESHOLD &&
    distinctEncounterYears(folder.groups) >= 2;
  return (
    <div className="px-1.5 pb-1.5 pt-1">
      {yearGrouped ? (
        <YearGroupedBody folder={folder} ctx={ctx} />
      ) : (
        <ul className="space-y-1">
          {folder.groups.map((group) => (
            <TitleGroupRows
              key={group.latest.id}
              group={group}
              ctx={ctx}
              inFolder
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** One folder list item: single-line collapsed row when collapsed, or header + expanded body when expanded. */
function SourceFolderItem({
  folder,
  expanded,
  onToggle,
  expandedSubfolders,
  onToggleSubfolder,
  ctx,
}: {
  folder: SourceFolder;
  expanded: boolean;
  onToggle: () => void;
  expandedSubfolders?: Set<string>;
  onToggleSubfolder?: (key: string) => void;
  ctx: RowContext;
}): React.ReactElement {
  const showException = ctx.mixed && folder.hasPending;
  const latestDoc = folder.groups[0]?.latest;
  const latestId = latestDoc?.id;
  const latestDateObj = latestDoc
    ? parseEncounterDateFromTitle(latestDoc.title)
    : null;
  const latestDate = latestDateObj ? formatDate(latestDateObj) : "";

  // Selected state: true if the currently selected document is anywhere inside this folder
  const isSelected = folder.groups.some(
    (g) =>
      g.latest.id === ctx.selectedId ||
      g.earlier.some((d) => d.id === ctx.selectedId),
  );

  if (!expanded) {
    return (
      <li data-testid="source-folder">
        <div
          data-testid="source-folder-collapsed-row"
          onClick={() => {
            if (latestId) {
              ctx.onSelect(latestId);
            }
          }}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer select-none transition-colors text-sm ${
            isSelected
              ? "bg-blue-50 border-blue-300 font-medium"
              : "border-slate-200 hover:bg-slate-50"
          }`}
        >
          <span className="text-xs shrink-0 select-none" aria-hidden="true">
            {sourceKindIcon(folder.key)}
          </span>
          <span className="flex-1 min-w-0 text-xs font-semibold text-slate-700 truncate">
            {folder.label}
          </span>
          {showException ? (
            <span
              data-testid="source-folder-exception"
              title="Some sources in this folder have not been reviewed for the clinical profile."
              className="shrink-0 rounded-full bg-amber-100 border border-amber-300 px-1.5 text-[10px] font-semibold text-amber-800"
            >
              {humanizeStatus("not_ingested")}
            </span>
          ) : null}
          {latestDate ? (
            <span
              data-testid="source-row-date"
              className="shrink-0 text-[11px] text-slate-500 tabular-nums"
            >
              {latestDate}
            </span>
          ) : null}
          <span
            data-testid="source-folder-count"
            className="text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-1.5"
          >
            {folder.documentCount}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            aria-label={`Expand ${folder.label}`}
            data-testid="source-folder-toggle"
            className="chevron text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded w-5 h-5 grid place-items-center transition-transform"
          >
            ›
          </button>
        </div>
      </li>
    );
  }

  return (
    <li
      data-testid="source-folder"
      className="rounded-lg border border-slate-200"
    >
      <FolderHeader
        folder={folder}
        expanded={expanded}
        onToggle={onToggle}
        onSelect={ctx.onSelect}
        showException={showException}
      />
      <FolderBody
        folder={folder}
        expandedSubfolders={expandedSubfolders}
        onToggleSubfolder={onToggleSubfolder}
        ctx={ctx}
      />
    </li>
  );
}

/**
 * Render the source list with upload + select.
 *
 * @param props - The documents, selection, and select/upload handlers.
 * @returns The source-list element.
 */
export const SourceList: React.FC<SourceListProps> = ({
  documents,
  selectedId,
  onSelect,
  searchQuery = "",
  patientId,
  sortField = "date",
  sortDirection = "desc",
}) => {
  // Memoized keyed on `documents`: the grouping/parsing pipeline below runs
  // over the patient's full source list (hundreds of documents), and without
  // memoization it reran on every render — any unrelated state change
  // (selection, hover, a folder toggle) redid the whole pipeline.
  const allSources = useMemo(
    () => documents.filter((d) => d.type === "source"),
    [documents],
  );

  const normalizedQuery = (searchQuery ?? "").trim().toLowerCase();

  const filteredSources = useMemo(() => {
    if (normalizedQuery === "") return allSources;
    return allSources.filter((d) => {
      const titleMatch = d.title.toLowerCase().includes(normalizedQuery);
      const kindMatch =
        d.source_kind?.toLowerCase().includes(normalizedQuery) ?? false;
      const date = parseEncounterDateFromTitle(d.title);
      const dateMatch = date
        ? date.toISOString().slice(0, 10).includes(normalizedQuery)
        : false;
      return titleMatch || kindMatch || dateMatch;
    });
  }, [allSources, normalizedQuery]);

  // BLA-348: this fold (title-grouping -> dimension-folding) is the
  // previously-measured Sources-tab client-side bottleneck, proven NOT to be
  // a backend problem — so its duration is recorded separately from the
  // `sources.fetch_list` server round-trip (see `useDocumentLibrary.ts`),
  // keyed by input size to distinguish "slow because large" from "slow
  // regardless of size." Both halves run inside ONE memo so the recorded
  // duration covers the whole derivation the metric is named for —
  // `groupSourcesByTitle`'s per-title `localeCompare` sort is a real share of
  // the cost, and timing only the second half would under-report it. An empty
  // input is NOT recorded: this component renders once with `[]` while the
  // list is still loading, and a ~0ms fold over nothing is not a sample of
  // the operation — shipping it would halve the metric's published p50.
  const folders = useMemo(() => {
    // Deliberate, scoped impurity: this measures the memoized computation's
    // own wall-clock cost (BLA-348) and reports it as a side channel via
    // `recordDuration` — it does not affect `result`, so the memo stays
    // referentially pure with respect to its actual output.
    /* eslint-disable-next-line react-hooks/purity */
    const start = performance.now();
    const titleGroups = groupSourcesByTitle(filteredSources);
    const result = groupTitleGroupsByDimension(titleGroups, kindDimension, {
      sortField,
      sortDirection,
    });
    if (filteredSources.length > 0) {
      /* eslint-disable-next-line react-hooks/purity */
      recordDuration("sources.client_derivation", performance.now() - start, {
        source_count: filteredSources.length,
        title_group_count: titleGroups.length,
      });
    }
    return result;
  }, [filteredSources, sortField, sortDirection]);

  // Option B: Tier 2 Content Search (deep note body search with debounced server query)
  const [contentResults, setContentResults] = useState<ContentSearchResult[]>(
    [],
  );

  useEffect(() => {
    if (!patientId || normalizedQuery.length < 2) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setContentResults([]);
      return;
    }

    let active = true;
    const timer = setTimeout(async () => {
      try {
        const resp = await apiUtils.fetchData<{
          results: ContentSearchResult[];
        }>(
          `/api/documents/search?patient_id=${encodeURIComponent(
            patientId,
          )}&q=${encodeURIComponent(normalizedQuery)}&type=source`,
          "GET",
        );
        if (!active) return;
        if (!isApiError(resp) && resp?.results) {
          setContentResults(resp.results);
        } else {
          setContentResults([]);
        }
      } catch {
        if (active) setContentResults([]);
      }
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [patientId, normalizedQuery]);

  const total = allSources.length;
  const notIngestedCount = useMemo(
    () =>
      allSources.filter((d) => d.ingestion_status === "not_ingested").length,
    [allSources],
  );
  // BLA-332: the "not yet added to profile" ingestion-status signalling below
  // (badge/tooltip/folder-exception/panel-summary) is honest signalling for the
  // now-hidden Blueprint/profile-ingestion pipeline (SHOW_BLUEPRINT_EXPLORE_TABS)
  // — showing it while that pipeline's own surface is hidden would reference a
  // concept the clinician can no longer see or act on. Forcing both flags false
  // here suppresses every downstream badge/tooltip/summary in one place; if
  // Blueprint returns, flip the switch and this signalling returns with it.
  // Every source shares one state → state it once (below), no per-row repetition.
  const allNotIngested =
    SHOW_BLUEPRINT_EXPLORE_TABS && total > 0 && notIngestedCount === total;
  // Mixed states → a per-row/-folder badge on the deviating (not-ingested) items.
  const mixed =
    SHOW_BLUEPRINT_EXPLORE_TABS && notIngestedCount > 0 && !allNotIngested;

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [expandedSubfolders, setExpandedSubfolders] = useState<Set<string>>(
    new Set(),
  );

  // Auto-expand the "Assessments" parent folder AND the specific instrument subfolder
  // if an assessment document is selected (e.g. via citation reveal)
  useEffect(() => {
    if (selectedId) {
      const selectedDoc = allSources.find(
        (d) => d.id === selectedId && isAssessmentKind(d.source_kind),
      );
      if (selectedDoc) {
        /* eslint-disable-next-line react-hooks/set-state-in-effect */
        setExpandedFolders((prev) =>
          prev.has(ASSESSMENTS_FOLDER_KEY)
            ? prev
            : new Set(prev).add(ASSESSMENTS_FOLDER_KEY),
        );
        const subfolderKey = selectedDoc.source_kind ?? "unknown";
        setExpandedSubfolders((prev) =>
          prev.has(subfolderKey) ? prev : new Set(prev).add(subfolderKey),
        );
      }
    }
  }, [selectedId, allSources]);

  const toggleExpandedFolder = (key: string): void => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleExpandedSubfolder = (key: string): void => {
    setExpandedSubfolders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const ctxForFolder = (): RowContext => ({
    selectedId,
    onSelect,
    mixed,
  });

  return (
    <div className="flex flex-col space-y-3">
      {normalizedQuery !== "" && (
        <div
          data-testid="sources-search-match-count"
          className="flex items-center justify-between text-[11px] text-slate-500 px-0.5 pb-1"
        >
          <span>
            {filteredSources.length === 1
              ? "1 matching encounter"
              : `${filteredSources.length} matching encounters`}
          </span>
        </div>
      )}
      {allNotIngested ? (
        <div
          data-testid="source-status-summary"
          title="These sources have not been reviewed for the clinical profile — nothing from them has been added yet."
          className="flex items-center gap-1 px-0.5 pb-1"
        >
          <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />
          <span className="text-[10px] text-amber-700 leading-tight truncate">
            {`${total} not yet in profile`}
          </span>
        </div>
      ) : null}
      {normalizedQuery !== "" && filteredSources.length === 0 ? (
        <p
          className="text-sm text-slate-400 italic py-2"
          data-testid="sources-search-no-matches"
        >
          No sources match &ldquo;{searchQuery?.trim()}&rdquo;
        </p>
      ) : folders.length === 0 ? (
        <p className="text-sm text-slate-400 italic">
          No sources uploaded yet.
        </p>
      ) : (
        <ul className="space-y-1" data-testid="source-list">
          {folders.map((folder) =>
            folder.groups.length === 1 &&
            folder.key !== ASSESSMENTS_FOLDER_KEY ? (
              <TitleGroupRows
                key={folder.key}
                group={folder.groups[0]}
                ctx={ctxForFolder()}
                inFolder={false}
              />
            ) : (
              <SourceFolderItem
                key={folder.key}
                folder={folder}
                expanded={expandedFolders.has(folder.key)}
                onToggle={() => toggleExpandedFolder(folder.key)}
                expandedSubfolders={expandedSubfolders}
                onToggleSubfolder={toggleExpandedSubfolder}
                ctx={ctxForFolder()}
              />
            ),
          )}
        </ul>
      )}

      {contentResults.length > 0 && (
        <div
          className="pt-2 border-t border-slate-200 space-y-1.5"
          data-testid="sources-search-content-results"
        >
          <div className="flex items-center justify-between text-[10px] uppercase font-bold text-slate-400 px-1">
            <span>Body Mentions ({contentResults.length} notes)</span>
          </div>
          <div className="space-y-1">
            {contentResults.map((result, idx) => (
              <div
                key={`${result.document_id}-${idx}`}
                onClick={() => onSelect(result.document_id, result.snippet)}
                data-testid="sources-content-result-row"
                className="p-1.5 rounded bg-slate-50 border border-slate-200 text-[11px] space-y-0.5 cursor-pointer hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center justify-between font-semibold text-slate-700 text-[10px]">
                  <span className="truncate">{result.title}</span>
                  {result.encounter_date && (
                    <span className="text-[10px] text-slate-400 whitespace-nowrap ml-1">
                      {result.encounter_date}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-600 leading-snug line-clamp-2">
                  &ldquo;{result.snippet}&rdquo;
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceList;
